import type { ColDef } from "ag-grid-enterprise";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import Button from "../Button";
import Input from "../Input";

const ItemType = "COLUMN";

const DraggableItem = ({
  item,
  index,
  moveItem,
  toggleChecked,
  onDragEnd,
}: {
  index: number;
  item: ColDef;
  moveItem: (draggedItemIdx: number, itemIdx: number) => void;
  onDragEnd: () => void;
  toggleChecked: (colId: string) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => {
      onDragEnd();
    },
    item: () => ({ index }),
    type: ItemType,
  });

  const [, drop] = useDrop({
    accept: ItemType,
    hover: (draggedItem: { index: number }, monitor) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveItem(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={clsx(
        "flex items-center gap-2 cursor-grab p-1 hover:bg-gray-100",
        isDragging && "opacity-50",
      )}
    >
      <span className="text-gray-500">☰</span>
      <Input
        type="checkbox"
        rightLabel={item.headerName}
        onChange={() => toggleChecked(item.colId!)}
        value={!item.hide ? "true" : "false"}
      />
    </div>
  );
};

const ColumnDropdown = ({
  columnDefs,
  setColumnDefs,
}: {
  columnDefs: ColDef[];
  setColumnDefs: (defs: ColDef[]) => void;
}) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [localColumnDefs, setLocalColumnDefs] = useState(columnDefs);
  const areAllColumnsSelected =
    localColumnDefs.filter((col) => !col.hide).length ===
    localColumnDefs.length;
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalColumnDefs(columnDefs);
  }, [columnDefs]);

  const toggleChecked = (colId: string) => {
    const newDefs = localColumnDefs.map((col) =>
      col.colId === colId ? { ...col, hide: !col.hide } : col,
    );

    setLocalColumnDefs(newDefs);
    setColumnDefs(newDefs);
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    setLocalColumnDefs((prev) => {
      const result = [...prev];
      const [removed] = result.splice(fromIndex, 1);
      result.splice(toIndex, 0, removed);
      return result;
    });
  };

  const handleDragEnd = () => {
    setColumnDefs(localColumnDefs);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const onToggleAll = () => {
    const newDefs = localColumnDefs.map((col) => {
      return { ...col, hide: areAllColumnsSelected };
    });
    setLocalColumnDefs(newDefs);
    setColumnDefs(newDefs);
  };

  const filteredColumns = useMemo(() => {
    const cols = localColumnDefs.filter((col) =>
      col.headerName?.toLowerCase().includes(search.toLowerCase()),
    );
    return cols;
  }, [search, localColumnDefs]);
  return (
    <DndProvider backend={HTML5Backend}>
      <div className="relative" ref={dropdownRef}>
        <Button plain icon="fasCog" onClick={() => setIsOpen(!isOpen)}>
          Columns
        </Button>

        {isOpen && (
          <div className="absolute top-10 right-0 w-[350px] bg-white border shadow-lg rounded z-10">
            <div className="relative flex items-center justify-between p-2">
              <Input
                type="search"
                placeholder="Search columns"
                value={search}
                onChange={(value) => setSearch(value)}
              />
              {!search && (
                <Button onClick={onToggleAll} plain>
                  {areAllColumnsSelected ? "Clear All" : "Select All"}
                </Button>
              )}
            </div>

            <div className="px-[10px] max-h-[300px] overflow-y-auto">
              <div>
                {filteredColumns.map((col, index) => (
                  <DraggableItem
                    key={col.colId || index}
                    item={col}
                    index={index}
                    moveItem={moveItem}
                    toggleChecked={() => toggleChecked(col.colId!)}
                    onDragEnd={handleDragEnd}
                  />
                ))}
              </div>
            </div>

            <div className="border w-full flex justify-end pb-2 pr-2">
              <Button className="mt-2 p-1" onClick={() => setIsOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

export default ColumnDropdown;
